<template>
  <AppShell title="Ansökan">
    <template #actions>
      <AppRouterLink to="/ansokningar" variant="button" class="back-link"> Tillbaka till ansökningar </AppRouterLink>
    </template>

    <div ref="stickyHeaderEl" class="sticky-header">
      <ApplicationHeader :application-id="applicationId" />

      <AppTabs :is-loading="isLoading" class="app-tabs-override">
        <template #left>
          <AppRouterLink
            :to="basePath"
            variant="nav"
            class="nav-link"
            exact-active-class="nav-link-active font-semibold"
          >
            Ansökan
          </AppRouterLink>
        </template>
        <template #right>
          <AppRouterLink
            :to="`${basePath}/dokument`"
            variant="nav"
            class="nav-link"
            active-class="nav-link-active font-semibold"
          >
            Dokument
          </AppRouterLink>
          <AppRouterLink
            :to="`${basePath}/kompletteringar`"
            variant="nav"
            class="nav-link"
            active-class="nav-link-active font-semibold"
          >
            Kompletteringar
          </AppRouterLink>
        </template>
      </AppTabs>
    </div>

    <router-view />
  </AppShell>
</template>

<script setup lang="ts">
import { computed, onMounted, provide, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { AppTabs } from '@staff/ui'
import AppShell from '@/components/AppShell.vue'
import AppRouterLink from '@/components/AppRouterLink.vue'
import ApplicationHeader from './ApplicationHeader.vue'
import { provideApplicationContext, provideApplicationKalpContext } from './application.context'
import { provideApplicationDocumentsContext } from './applicationDocuments.context'
import { useStickyHeader } from '@/composables/useStickyHeader'

import {
  fetchApplicationById,
  fetchApplicationKalp,
  type DistributorApplicationDetails,
  type KalpCalculation,
} from '@/services/application'

const route = useRoute()

const isLoading = ref(true)
const error = ref('')
const application = ref<DistributorApplicationDetails | null>(null)
const kalp = ref<KalpCalculation | null>(null)
const isKalpLoading = ref(true)
const kalpError = ref('')
provideApplicationKalpContext({ kalp, isKalpLoading, kalpError })

const basePath = computed(() => `/ansokningar/${route.params.id}`)
const applicationId = computed(() => String(route.params.id || ''))
provideApplicationContext({
  application,
  isLoading,
  error,
})
provideApplicationDocumentsContext(applicationId)

const { stickyHeaderEl, observeHeader } = useStickyHeader(isLoading)

let loadVersion = 0

const loadKalp = async (id: string, version: number) => {
  try {
    const response = await fetchApplicationKalp(id)
    if (version === loadVersion && id === applicationId.value) kalp.value = response.maxLoan
  } catch {
    if (version === loadVersion && id === applicationId.value) kalpError.value = 'Kunde inte hämta KALP-beräkningen.'
  } finally {
    if (version === loadVersion && id === applicationId.value) isKalpLoading.value = false
  }
}

const loadApplication = async () => {
  const version = ++loadVersion
  const id = applicationId.value
  application.value = null
  kalp.value = null
  error.value = ''
  kalpError.value = ''
  isLoading.value = Boolean(id)
  isKalpLoading.value = Boolean(id)

  if (!id) {
    error.value = 'Ansökan saknar ID i URL.'
    return
  }

  // Load independently so a slow or failed KALP does not hide the application.
  // Both the summary and modal consume this single calculation.
  const kalpRequest = loadKalp(id, version)
  try {
    const response = await fetchApplicationById(id)
    if (version === loadVersion && id === applicationId.value) application.value = response
  } catch (err) {
    if (version === loadVersion && id === applicationId.value) {
      error.value = err instanceof Error ? err.message : 'Ett fel uppstod när ansökan hämtades.'
    }
  } finally {
    if (version === loadVersion && id === applicationId.value) isLoading.value = false
  }
  await kalpRequest
}

provide('refreshApplication', loadApplication)

watch(applicationId, loadApplication)

onMounted(async () => {
  await loadApplication()
  observeHeader()
})
</script>

<style scoped lang="scss">
.sticky-header {
  position: sticky;
  /* Vertical offsets must match .app-shell-content's padding-top (12px, 8px on
     small screens), horizontal must match .app-shell-main's padding (24px/16px). */
  top: -12px;
  z-index: 10;
  background-color: var(--color-canvas-inset); /* matches AppShell main */
  margin: -12px -24px 0;
  padding: 12px 24px 0;
}

.app-tabs-override :deep(.app-tabs-wrapper) {
  position: static;
}

.back-link:hover,
.nav-link:hover {
  background-color: var(--color-button-default-hover-bg);
}

.nav-link-active {
  background-color: var(--color-brand-subtle);
  color: var(--color-brand-default);
}

@media (max-width: 767px) {
  .sticky-header {
    top: -8px;
    margin: -8px -16px 0;
    padding: 8px 16px 0;
  }
}
</style>
